import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';

import { UserService } from 'src/app/services/user/user.service';
import { SapService } from 'src/app/services/sap/sap.service';
import { SAP_RULES, validateSapField } from 'src/app/services/sap/sap-rules';

/**
 * Submódulo "Gestión en SAP" del cliente (ruta console/person/sap/:code).
 * Vistas: Análisis, Crear, Modificar (formulario editable) y Bitácora.
 */
@Component({
  selector: 'app-contact-sap',
  templateUrl: './contact-sap.component.html',
  styleUrls: [],
})
export class ContactSapComponent implements OnInit {
  code = '';
  person: any = null;

  loading = false;
  analyzing = false;
  processing = false;

  // Resultado del análisis
  apto: boolean | null = null;
  aprobado: boolean | null = null;         // approve APPROVED+COMPLETE (de /analyze): sin esto no se puede crear en SAP
  errores: any[] = [];
  payload: any = null;            // payload que se enviaría a SAP (armado por el backend)
  businessPartner = '';

  // Estado SAP guardado en la BD
  get sap() { return this.person?.person?.sap || null; }
  get isCreated(): boolean { return !!this.sap?.fe_c; }
  /** Cuentas bancarias que van en el request de modificación (vienen del apartado real de cuentas). */
  get modifyBanks(): any[] { return (this.payload && this.payload.d && this.payload.d.to_BusinessPartnerBank && this.payload.d.to_BusinessPartnerBank.results) || []; }

  // Navegación de pestañas
  activeTab: 'analisis' | 'crear' | 'modificar' | 'bitacora' | 'masiva' | 'config' = 'analisis';

  // Configuración de endpoints (una URL por operación)
  config: any = null;
  configForm: any = { url_csrf: '', url_create: '', url_modify: '', method_create: 'POST', method_modify: 'PATCH', use_csrf: true, auth_user: '', auth_pass: '', timeout_ms: 20000, enabled: true };
  loadingConfig = false;
  savingConfig = false;

  // Formulario de modificación (editable)
  edit: any = null;
  editErrors: { [field: string]: string } = {};

  // Bitácora
  logs: any[] = [];
  loadingLogs = false;

  // Catálogos para los selects
  readonly OCCUPATIONS = [
    { code: '0001', name: 'NEGOCIO PROPIO' }, { code: '0002', name: 'PROFESIONAL INDEPENDIENTE' },
    { code: '0003', name: 'EMPLEADO' }, { code: '0004', name: 'JUBILADO' }, { code: '0005', name: 'PENSIONADO' },
    { code: '0006', name: 'ESTUDIANTE' }, { code: '0007', name: 'AMA DE CASA' }, { code: '0008', name: 'DESEMPLEADO' },
  ];
  readonly MARITAL = [
    { code: '1', name: 'Soltero/a' }, { code: '2', name: 'Casado/a' }, { code: '3', name: 'Viudo/a' }, { code: '4', name: 'Divorciado/a' },
  ];
  readonly PHONE_TYPES = [{ code: '1', name: 'Fijo' }, { code: '2', name: 'Celular' }];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _user: UserService,
    private sapService: SapService,
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((p) => {
      this.code = p['code'];
      if (this.code) this.init();
    });
  }

  private async init(): Promise<void> {
    this.loading = true;
    try {
      const resp = await this._user.getPersonbyCode({ code: this.code });
      this.person = resp?.resp || null;
    } catch (_) {}
    this.loading = false;
    await this.runAnalyze();
  }

  goBack(): void {
    this.router.navigate(['/console/person/profile', this.code]);
  }

  fullName(): string {
    const p = this.person?.person;
    return p ? `${p.name || ''} ${p.lastName || ''}`.trim() : this.code;
  }

  // ---- Análisis ----------------------------------------------------------
  async runAnalyze(): Promise<void> {
    this.analyzing = true;
    try {
      const r = await this.sapService.analyze(this.code);
      this.apto = !!r?.apto;
      this.aprobado = r?.aprobado === undefined ? null : !!r.aprobado;
      this.errores = r?.errores || [];
      this.payload = r?.payload || null;
      this.businessPartner = r?.business_partner || '';
      this.buildEditModel();
    } catch (e: any) {
      this.apto = null;
      this.errores = [];
      Swal.fire({ icon: 'error', title: 'Error', text: e?.error?.message || 'No se pudo analizar el cliente.' });
    } finally {
      this.analyzing = false;
    }
  }

  // ---- Crear -------------------------------------------------------------
  async doCreate(): Promise<void> {
    if (this.processing || !this.apto || this.aprobado === false) return;
    const c = await Swal.fire({
      title: this.isCreated ? '¿Reenviar el alta a SAP?' : '¿Crear cliente en SAP?',
      html: `<b>${escapeHtml(this.fullName())}</b><br>Business Partner: <b>${escapeHtml(this.businessPartner)}</b>`
        + (this.isCreated ? '<br><br>Este cliente ya figura como creado. Si SAP responde que ya existe, se mantiene como creado.' : ''),
      icon: 'question', showCancelButton: true, confirmButtonText: 'Sí, crear', cancelButtonText: 'Cancelar', confirmButtonColor: '#16a34a',
    });
    if (!c.isConfirmed) return;
    this.processing = true;
    Swal.fire({ title: 'Creando en SAP…', didOpen: () => Swal.showLoading(), allowOutsideClick: false });
    try {
      const r = await this.sapService.create(this.code);
      await Swal.fire({ icon: 'success', title: r?.alreadyExists ? 'El cliente ya existe en SAP' : 'Cliente creado en SAP', html: `${escapeHtml(r?.message || '')}<br>Business Partner: <b>${escapeHtml(r?.data?.business_partner)}</b>` });
      await this.init();
      this.activeTab = 'modificar';
    } catch (e: any) {
      this.showError(e, 'No se pudo crear el cliente.');
    } finally {
      this.processing = false;
    }
  }

  // ---- Modificar (formulario editable) -----------------------------------
  private buildEditModel(): void {
    const d = this.payload?.d;
    if (!d) { this.edit = null; return; }
    const addr = (d.to_BusinessPartnerAddress?.results || [])[0] || {};
    const email = (addr.to_EmailAddress?.results || [])[0] || {};
    const phones = addr.to_PhoneNumber?.results || [];
    this.edit = {
      FirstName: d.FirstName || '',
      LastName: d.LastName || '',
      BusPartMaritalStatus: d.BusPartMaritalStatus || '',
      birthDate: (d.BirthDate || '').substring(0, 10),   // YYYY-MM-DD para el input date
      BusinessPartnerOccupation: d.BusinessPartnerOccupation || '',
      NaturalPersonEmployerName: d.NaturalPersonEmployerName || '',
      Country: addr.Country || 'VE',
      Region: addr.Region || '',
      CityName: addr.CityName || '',
      StreetName: addr.StreetName || '',
      EmailAddress: email.EmailAddress || '',
      phone1: phones[0]?.PhoneNumber || '',
      phone1Type: phones[0]?.PhoneNumberType || '2',
      phone2: phones[1]?.PhoneNumber || '',
      phone2Type: phones[1]?.PhoneNumberType || '1',
    };
    this.editErrors = {};
  }

  /** Valida un campo del formulario en línea (usa las reglas del documento). */
  validateEdit(field: string): void {
    const map: { [k: string]: string } = {
      FirstName: 'FirstName', LastName: 'LastName', BusPartMaritalStatus: 'BusPartMaritalStatus',
      BusinessPartnerOccupation: 'BusinessPartnerOccupation', NaturalPersonEmployerName: 'NaturalPersonEmployerName',
      Country: 'Country', Region: 'Region', CityName: 'CityName', StreetName: 'StreetName',
      EmailAddress: 'EmailAddress', phone1: 'PhoneNumber', phone2: 'PhoneNumber', phone1Type: 'PhoneNumberType', phone2Type: 'PhoneNumberType',
    };
    const rule = map[field];
    if (!rule) return;
    const err = validateSapField(rule, this.edit[field]);
    if (err) this.editErrors[field] = err.message;
    else delete this.editErrors[field];
  }

  validateAllEdit(): boolean {
    // Valida los campos EDITABLES (los que van en el JSON de modificación).
    // Empresa es opcional; profesión y fecha usan selects/date (válidos por construcción);
    // el banco lo valida el backend al guardar.
    this.editErrors = {};
    ['EmailAddress', 'phone1', 'phone2', 'NaturalPersonEmployerName'].forEach((f) => this.validateEdit(f));
    return Object.keys(this.editErrors).length === 0;
  }

  get editHasErrors(): boolean { return Object.keys(this.editErrors).length > 0; }

  private buildModifyPayload(): any {
    const e = this.edit;
    // Se parte del payload analizado y se sobreescriben los campos editados;
    // así se conservan sexo, banco e identidad (que el backend además fuerza).
    const base = JSON.parse(JSON.stringify(this.payload.d));
    base.FirstName = (e.FirstName || '').toUpperCase();
    base.LastName = (e.LastName || '').toUpperCase();
    base.BusPartMaritalStatus = e.BusPartMaritalStatus;
    base.BirthDate = e.birthDate ? `${e.birthDate}T00:00:00` : '';
    base.BusinessPartnerOccupation = e.BusinessPartnerOccupation;
    base.NaturalPersonEmployerName = (e.NaturalPersonEmployerName || '').toUpperCase();

    const addr = (base.to_BusinessPartnerAddress?.results || [])[0] || {};
    addr.Country = (e.Country || '').toUpperCase();
    addr.Region = (e.Region || '').toUpperCase();
    addr.CityName = (e.CityName || '').toUpperCase();
    addr.StreetName = (e.StreetName || '').toUpperCase();
    if (!addr.to_EmailAddress) addr.to_EmailAddress = { results: [] };
    addr.to_EmailAddress.results = [{ OrdinalNumber: '1', IsDefaultEmailAddress: true, EmailAddress: e.EmailAddress }];
    // PhoneNumberType SIEMPRE '1'. Si solo hay uno, se envía también como segundo.
    const phones: any[] = [];
    if (e.phone1) phones.push({ OrdinalNumber: '1', IsDefaultPhoneNumber: true, PhoneNumber: e.phone1, PhoneNumberType: '1' });
    if (e.phone2) phones.push({ OrdinalNumber: String(phones.length + 1), IsDefaultPhoneNumber: false, PhoneNumber: e.phone2, PhoneNumberType: '1' });
    if (phones.length === 1) phones.push({ OrdinalNumber: '2', IsDefaultPhoneNumber: false, PhoneNumber: phones[0].PhoneNumber, PhoneNumberType: '1' });
    addr.to_PhoneNumber = { results: phones };
    base.to_BusinessPartnerAddress = { results: [addr] };
    // Omitir la cuenta bancaria si no hay ninguna (no se envía vacía).
    if (base.to_BusinessPartnerBank && !(base.to_BusinessPartnerBank.results || []).length) delete base.to_BusinessPartnerBank;
    return { d: base };
  }

  async doModify(): Promise<void> {
    if (this.processing || !this.isCreated) return;
    if (!this.validateAllEdit()) {
      Swal.fire({ icon: 'warning', title: 'Revisa el formulario', text: 'Hay campos con datos inválidos.' });
      return;
    }
    const c = await Swal.fire({
      title: '¿Guardar cambios en SAP?',
      html: `<b>${escapeHtml(this.fullName())}</b><br>Business Partner: <b>${escapeHtml(this.sap?.business_partner)}</b>`,
      icon: 'question', showCancelButton: true, confirmButtonText: 'Sí, guardar', cancelButtonText: 'Cancelar', confirmButtonColor: '#d97706',
    });
    if (!c.isConfirmed) return;
    this.processing = true;
    Swal.fire({ title: 'Guardando en SAP…', didOpen: () => Swal.showLoading(), allowOutsideClick: false });
    try {
      const r = await this.sapService.modify(this.code, this.buildModifyPayload());
      await Swal.fire({ icon: 'success', title: 'Cliente actualizado en SAP', text: 'Los cambios se guardaron correctamente.' });
      await this.init();
    } catch (e: any) {
      this.showError(e, 'No se pudo modificar el cliente.');
    } finally {
      this.processing = false;
    }
  }

  // ---- Bitácora ----------------------------------------------------------
  async loadLogs(): Promise<void> {
    this.loadingLogs = true;
    try {
      const r = await this.sapService.logs(this.code);
      this.logs = r?.logs || [];
    } catch (_) {
      this.logs = [];
    } finally {
      this.loadingLogs = false;
    }
  }

  setTab(tab: 'analisis' | 'crear' | 'modificar' | 'bitacora' | 'masiva' | 'config'): void {
    this.activeTab = tab;
    if (tab === 'bitacora') this.loadLogs();
    if (tab === 'config') this.loadConfig();
  }

  actionLabel(a: string): string {
    return a === 'create' ? 'Creación' : a === 'modify' ? 'Modificación' : 'Análisis';
  }

  /** Modal con el request enviado a SAP y la respuesta recibida. */
  openLogDetail(l: any): void {
    const pretty = (o: any) => {
      try { return JSON.stringify(o, null, 2); } catch { return String(o); }
    };
    Swal.fire({
      title: `${this.actionLabel(l.action)} · ${l.success ? 'OK' : 'Falló'}`,
      html: `
        <div style="text-align:left">
          <div class="text-muted" style="font-size:12px">${escapeHtml(l.createdAt ? new Date(l.createdAt).toLocaleString('es-VE') : '')} · ${escapeHtml(l.username || '—')} · BP ${escapeHtml(l.business_partner || '—')}</div>
          <div style="margin-top:8px"><b>URL:</b> <span style="font-size:12px">${escapeHtml(l.sap_url || l.endpoint || '—')}</span></div>
          <h6 style="margin-top:10px">Request (enviado a SAP)</h6>
          <pre style="max-height:200px;overflow:auto;background:#0d1117;color:#c9d1d9;padding:10px;border-radius:6px;font-size:11px">${escapeHtml(pretty(l.request_payload))}</pre>
          <h6 style="margin-top:10px">Respuesta de SAP</h6>
          <pre style="max-height:200px;overflow:auto;background:#0d1117;color:#c9d1d9;padding:10px;border-radius:6px;font-size:11px">${escapeHtml(l.sap_response != null ? pretty(l.sap_response) : '(sin respuesta de SAP)')}</pre>
          <h6 style="margin-top:10px">Resultado (backend)</h6>
          <pre style="max-height:160px;overflow:auto;background:#0d1117;color:#c9d1d9;padding:10px;border-radius:6px;font-size:11px">${escapeHtml(pretty(l.response_body))}</pre>
        </div>`,
      width: 760,
      confirmButtonText: 'Cerrar',
    });
  }

  // ---- Configuración de endpoints ----------------------------------------
  async loadConfig(): Promise<void> {
    this.loadingConfig = true;
    try {
      const r = await this.sapService.getConfig();
      this.config = r?.config || null;
      if (this.config) {
        this.configForm = {
          url_csrf: this.config.url_csrf || '',
          url_create: this.config.url_create || '',
          url_modify: this.config.url_modify || '',
          method_create: this.config.method_create || 'POST',
          method_modify: this.config.method_modify || 'PATCH',
          use_csrf: this.config.use_csrf !== false,
          auth_user: this.config.auth_user || '',
          auth_pass: this.config.auth_pass || '',
          timeout_ms: this.config.timeout_ms || 20000,
          enabled: this.config.enabled !== false,
        };
      }
    } catch (_) {
      this.config = null;
    } finally {
      this.loadingConfig = false;
    }
  }

  /** Rellena las 3 URLs a partir de una base (deriva las rutas OData estándar). */
  usePreset(base: string): void {
    const b = (base || '').replace(/\/+$/, '');
    const svc = '/sap/opu/odata/sap/API_BUSINESS_PARTNER';
    this.configForm.url_csrf = `${b}${svc}/`;
    this.configForm.url_create = `${b}${svc}/A_BusinessPartner`;
    this.configForm.url_modify = `${b}${svc}/A_BusinessPartner('{bp}')`;
    this.configForm.use_csrf = true;
    this.configForm.method_create = 'POST';
    this.configForm.method_modify = 'PATCH';
  }

  /** Preset SAP CPI (GT99): sin CSRF, crear=POST, modificar=PUT, misma URL. */
  useCpiPreset(): void {
    this.configForm.use_csrf = false;
    this.configForm.method_create = 'POST';
    this.configForm.method_modify = 'PUT';
  }

  /** Copia la URL de "Crear" a "Modificar" (en CPI es la misma). */
  copyCreateToModify(): void {
    this.configForm.url_modify = this.configForm.url_create;
  }

  async saveConfig(): Promise<void> {
    if (this.savingConfig) return;
    this.savingConfig = true;
    try {
      const r = await this.sapService.updateConfig(this.configForm);
      this.config = r?.config || this.config;
      await Swal.fire({ icon: 'success', title: 'Configuración guardada', html: `Las llamadas a SAP usarán las URLs configuradas.<br><small>${escapeHtml(this.config?.url_create || '')}</small>` });
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'Error', text: e?.error?.message || 'No se pudo guardar la configuración.' });
    } finally {
      this.savingConfig = false;
    }
  }

  private showError(e: any, fallback: string): void {
    const body = e?.error || {};
    const errores = body.errores || [];
    const list = errores.length ? `<ul style="text-align:left;max-height:300px;overflow:auto">${errores.map((x: any) => `<li>${escapeHtml(x.message)}</li>`).join('')}</ul>` : '';
    Swal.fire({ icon: 'error', title: 'Error', html: `${escapeHtml(body.message || e?.message || fallback)}${list}` });
  }
}

/** Escapa HTML para mostrar JSON crudo dentro del modal sin romper el markup. */
function escapeHtml(s: any): string {
  return (s == null ? '' : String(s))
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
